import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useCart } from '../context/CartContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Trash2, Plus, Minus, ShoppingBag } from 'lucide-react';

const Cart = () => {
  const { cart, removeFromCart, updateQuantity, getCartTotal, clearCart } = useCart();
  const navigate = useNavigate();

  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-black">
        <Navbar />
        <div className="pt-24 px-4 sm:px-6 lg:px-8 pb-20 flex items-center justify-center min-h-[80vh]">
          <Card className="bg-gradient-to-br from-purple-900/20 to-blue-900/20 border-white/10 max-w-md w-full">
            <CardContent className="p-8 text-center">
              <ShoppingBag className="mx-auto mb-4 text-purple-400" size={64} />
              <h2 className="text-2xl font-bold text-white mb-4">Your cart is empty</h2>
              <p className="text-gray-400 mb-6">
                Browse our products and add items to your cart.
              </p>
              <Link to="/products">
                <Button className="bg-purple-600 hover:bg-purple-700 text-white">
                  Browse Products
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      <Navbar />
      <div className="pt-24 px-4 sm:px-6 lg:px-8 pb-20">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <h1 className="text-4xl md:text-5xl font-bold text-white">Shopping Cart</h1>
            <Button
              onClick={clearCart}
              variant="outline"
              className="border-red-500/50 text-red-400 hover:bg-red-500/10"
            >
              Clear Cart
            </Button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Cart Items */}
            <div className="lg:col-span-2 space-y-4">
              {cart.map((item) => (
                <Card
                  key={item.id}
                  className="bg-gradient-to-br from-gray-900/50 to-gray-800/50 border-white/10"
                >
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-24 h-24 object-cover rounded-lg"
                      />
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-white mb-1">
                          {item.name}
                          <span className="text-purple-400">{item.game}</span>
                        </h3>
                        <p className="text-gray-400 text-sm mb-3">{item.duration}</p>
                        <div className="flex items-center space-x-4">
                          <div className="flex items-center space-x-2 bg-black/50 rounded-lg p-1">
                            <button
                              onClick={() => updateQuantity(item.id, item.quantity - 1)}
                              className="w-8 h-8 flex items-center justify-center text-white hover:bg-white/10 rounded transition-colors"
                            >
                              <Minus size={16} />
                            </button>
                            <span className="text-white font-semibold w-8 text-center">
                              {item.quantity}
                            </span>
                            <button
                              onClick={() => updateQuantity(item.id, item.quantity + 1)}
                              className="w-8 h-8 flex items-center justify-center text-white hover:bg-white/10 rounded transition-colors"
                            >
                              <Plus size={16} />
                            </button>
                          </div>
                          <button
                            onClick={() => removeFromCart(item.id)}
                            className="text-red-400 hover:text-red-300 transition-colors"
                          >
                            <Trash2 size={20} />
                          </button>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-purple-400">
                          ${(item.price * item.quantity).toFixed(2)}
                        </p>
                        <p className="text-gray-400 text-sm">${item.price.toFixed(2)} each</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <Card className="bg-gradient-to-br from-purple-900/20 to-blue-900/20 border-white/10 sticky top-24">
                <CardContent className="p-6">
                  <h3 className="text-2xl font-bold text-white mb-6">Order Summary</h3>
                  <div className="space-y-4 mb-6">
                    <div className="flex justify-between text-gray-400">
                      <span>Subtotal</span>
                      <span className="text-white">${getCartTotal().toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-gray-400">
                      <span>Tax</span>
                      <span className="text-white">$0.00</span>
                    </div>
                    <div className="border-t border-white/10 pt-4 flex justify-between">
                      <span className="text-xl font-bold text-white">Total</span>
                      <span className="text-2xl font-bold text-purple-400">
                        ${getCartTotal().toFixed(2)}
                      </span>
                    </div>
                  </div>
                  <Button
                    onClick={() => navigate('/checkout')}
                    className="w-full bg-purple-600 hover:bg-purple-700 text-white py-6 text-lg"
                  >
                    Proceed to Checkout
                  </Button>
                  <Link to="/products">
                    <Button
                      variant="outline"
                      className="w-full mt-3 border-white/20 text-white hover:bg-white/10"
                    >
                      Continue Shopping
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Cart;